import { createColumnHelper } from "@tanstack/react-table";
import { get } from "lodash-es";
import { useMemo } from "react";
import TeamCell from "@/components/headToHead/TeamStats/TeamCell";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import type {
  Maybe,
  BasicTeam,
  GetHeadToHeadTeamStatsTabQuery,
} from "@/graphql/gql/graphql";
import type {
  CellContext,
  ColumnDef,
  ColumnHelper,
} from "@tanstack/react-table";

interface Stat {
  rank: number;
  value: number;
}

type Data = {
  team: Pick<BasicTeam, "abbreviation" | "logoSVG">;
  season: {
    sacks: Stat;
    awaySacks: Stat;
    homeSacks: Stat;
    pdr: Stat;
    awayPDR: Stat;
    homePDR: Stat;
  };
  last3: {
    sacks: Stat;
    pdr: Stat;
  };
  last5: {
    sacks: Stat;
    pdr: Stat;
  };
};

export const teamToDefensiveStats = (team?: Maybe<BasicTeam>): Data => {
  const stats = get(team, "teamStats.defensiveStats", {});

  return {
    team: {
      abbreviation: get(team, "abbreviation", ""),
      logoSVG: get(team, "logoSVG", ""),
    },
    season: {
      sacks: {
        rank: get(stats, "rankedStats.rankedSacks.rank", 0),
        value: get(stats, "rankedStats.rankedSacks.value", 0),
      },
      awaySacks: {
        rank: get(stats, "rankedStats.rankedAwaySacks.rank", 0),
        value: get(stats, "rankedStats.rankedAwaySacks.value", 0),
      },
      homeSacks: {
        rank: get(stats, "rankedStats.rankedHomeSacks.rank", 0),
        value: get(stats, "rankedStats.rankedHomeSacks.value", 0),
      },
      pdr: {
        rank: get(stats, "rankedStats.rankedPDR.rank", 0),
        value: get(stats, "rankedStats.rankedPDR.value", 0),
      },
      awayPDR: {
        rank: get(stats, "rankedStats.rankedAwayPDR.rank", 0),
        value: get(stats, "rankedStats.rankedAwayPDR.value", 0),
      },
      homePDR: {
        rank: get(stats, "rankedStats.rankedHomePDR.rank", 0),
        value: get(stats, "rankedStats.rankedHomePDR.value", 0),
      },
    },
    last3: {
      sacks: {
        rank: get(stats, "last3Games.rankedSacks.rank", 0),
        value: get(stats, "last3Games.rankedSacks.value", 0),
      },
      pdr: {
        rank: get(stats, "last3Games.rankedPDR.rank", 0),
        value: get(stats, "last3Games.rankedPDR.value", 0),
      },
    },
    last5: {
      sacks: {
        rank: get(stats, "last5Games.rankedSacks.rank", 0),
        value: get(stats, "last5Games.rankedSacks.value", 0),
      },
      pdr: {
        rank: get(stats, "last5Games.rankedPDR.rank", 0),
        value: get(stats, "last5Games.rankedPDR.value", 0),
      },
    },
  };
};

export const createTableData = (
  data?: GetHeadToHeadTeamStatsTabQuery["event"]
) => {
  const homeTeam = data?.homeTeam;
  const awayTeam = data?.awayTeam;
  const homeStats = teamToDefensiveStats(homeTeam);
  const awayStats = teamToDefensiveStats(awayTeam);

  return [homeStats, awayStats];
};

const renderTeamCell = (row: CellContext<Data, BasicTeam>) => (
  <TeamCell
    abbreviation={row.getValue().abbreviation}
    logoSVG={row.getValue().logoSVG}
  />
);

const columnHelper = createColumnHelper<Data>();

type FirstArg<T extends (...args: never) => never> = Parameters<T>[0];
type AllowedKeys = FirstArg<ColumnHelper<Data>["accessor"]>;

const buildStatColumnGrouping = (
  valueKey: AllowedKeys,
  rankKey: AllowedKeys
): ColumnDef<Data>[] => [
  columnHelper.accessor(valueKey, {
    header: "Value",
  }),
  columnHelper.accessor(rankKey, {
    header: "Rank",
    cell: (row) => "#" + row.getValue(),
  }),
];

const createSackColumns = (seasonYear: number) => {
  return [
    columnHelper.group({
      id: "team",
      header: "",
      columns: [
        columnHelper.accessor("team", {
          header: "Team",
          cell: renderTeamCell,
        }),
      ],
    }),
    columnHelper.group({
      header: seasonYear.toString(),
      columns: buildStatColumnGrouping(
        "season.sacks.value",
        "season.sacks.rank"
      ),
    }),
    columnHelper.group({
      header: "Last 3",
      columns: buildStatColumnGrouping("last3.sacks.value", "last3.sacks.rank"),
    }),
    columnHelper.group({
      header: "Last 5",
      columns: buildStatColumnGrouping("last5.sacks.value", "last5.sacks.rank"),
    }),
    columnHelper.group({
      header: "Home",
      columns: buildStatColumnGrouping(
        "season.homeSacks.value",
        "season.homeSacks.rank"
      ),
    }),
    columnHelper.group({
      header: "Away",
      columns: buildStatColumnGrouping(
        "season.awaySacks.value",
        "season.awaySacks.rank"
      ),
    }),
  ];
};

const createPDRColumns = (seasonYear: number) => {
  return [
    columnHelper.group({
      id: "team",
      header: "",
      columns: [
        columnHelper.accessor("team", {
          header: "Team",
          cell: renderTeamCell,
        }),
      ],
    }),
    columnHelper.group({
      header: seasonYear.toString(),
      columns: buildStatColumnGrouping("season.pdr.value", "season.pdr.rank"),
    }),
    columnHelper.group({
      header: "Last 3",
      columns: buildStatColumnGrouping("last3.pdr.value", "last3.pdr.rank"),
    }),
    columnHelper.group({
      header: "Last 5",
      columns: buildStatColumnGrouping("last5.pdr.value", "last5.pdr.rank"),
    }),
    columnHelper.group({
      header: "Home",
      columns: buildStatColumnGrouping(
        "season.homePDR.value",
        "season.homePDR.rank"
      ),
    }),
    columnHelper.group({
      header: "Away",
      columns: buildStatColumnGrouping(
        "season.awayPDR.value",
        "season.awayPDR.rank"
      ),
    }),
  ];
};

export enum StatTab {
  Sacks = "Sacks",
  PassesDefensedRate = "Passes Defensed Rate",
}

const useDefensiveStatsTable = (
  data: GetHeadToHeadTeamStatsTabQuery["event"],
  tab: StatTab
) => {
  const { seasonYear } = useHeadToHeadContext();

  const columns = useMemo(
    (): ColumnDef<Data>[] =>
      (tab === StatTab.Sacks ? createSackColumns : createPDRColumns)(
        seasonYear
      ),
    [seasonYear, tab]
  );
  const tableData = useMemo(() => createTableData(data), [data]);

  return {
    columns,
    tableData,
  };
};

export default useDefensiveStatsTable;
